/**
 * App-shell copy — the signed-in product surface.
 *
 * Third source module in the catalog, alongside `marketing-copy.ts` (page-level
 * marketing) and `ui-copy.ts` (marketing chrome). All three are flattened into
 * `messages/en.json` by `npm run i18n:build`; this one lands under the `app.`
 * namespace.
 *
 * ── Why a separate module ───────────────────────────────────────────────────
 *
 * The split is by AUDIENCE, not by file size. Marketing copy is read by people
 * deciding whether to buy and is tuned for search and persuasion; app copy is
 * read by people who already bought and is tuned for doing a task. They get
 * edited for different reasons, by different instincts, and mixing them means a
 * pricing-page rewrite touches the same file as a button label.
 *
 * It also matters for translation: marketing strings carry CLAIMS and go through
 * the founder review gate (I18N_PLAN.md §6). App strings mostly name actions
 * ("Upload", "Delete", "Copy transcript") and do not. Keeping them apart is what
 * lets the review stay ~10 claim-bearing strings per locale instead of ~500.
 *
 * ── What does NOT belong here ───────────────────────────────────────────────
 *
 *   ❌ Admin (`/admin/*`). Internal, one operator, English forever. Translating
 *      it would roughly double this catalog to serve nobody.
 *   ❌ Anything with a `href` or an `id` in it — routing and identity are not
 *      language.
 *   ❌ Provider/model names, plan names, and other proper nouns.
 */

export const APP_COPY = {
  /** Sidebar navigation. Keyed by destination, not by label, so renaming the
   *  English word does not orphan eleven translations. */
  nav: {
    dashboard: 'Dashboard',
    inbox: 'Inbox',
    transcribe: 'Transcribe',
    upload: 'Upload',
    meetings: 'Meetings',
    transcripts: 'Transcripts',
    episodes: 'Episodes',
    library: 'Library',
    repurpose: 'Repurpose',
    exports: 'Exports',
    templates: 'Templates',
    textToSpeech: 'Text to speech',
    translate: 'Translate',
    media: 'Media',
    content: 'Content',
    support: 'Support',
    settings: 'Settings',
    home: 'Home',
    usage: 'Usage',
    billing: 'Billing',
    usageBilling: 'Usage & billing',
  },

  /** Tabs on a transcript. `id` stays English — it is a routing key. */
  tabs: {
    transcript: 'Transcript',
    summary: 'AI Summary',
    chat: 'Wisprs Chat',
    actions: 'Action Points',
    chapters: 'Chapters',
    minutes: 'Minutes',
    speakers: 'Speaker Summary',
    topics: 'Topic Summary',
    salesCallKit: 'Sales Call Kit',
  },

  /** The shell itself — sidebar affordances, the plan card, the signed-out gate. */
  shell: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    loading: 'Loading...',
    freePlan: 'Free Plan',
    minutesRemaining: 'Minutes remaining',
    /** Unit suffix after a minute count, e.g. "12 / 30 min". */
    minutesUnit: 'min',
    /** Appended to the unit on the free daily plan: "30 min today". */
    minutesUnitToday: 'min today',
    minutesRemainingToday: 'Minutes remaining today',
    usageUnavailable: 'Unable to load usage data',
    usageLoadFailed: 'Failed to load usage data',
    signInPrompt: 'Please sign in to continue',
    signIn: 'Sign In',
  },

  /**
   * Toasts — the transient confirmations and failures after an action.
   *
   * Keyed by OUTCOME, not by call site, so the same sentence is not translated
   * twelve times in nine files: `removedFromFolder` is raised from three
   * different screens and resolves to one catalog entry.
   *
   * Two things deliberately stay out:
   *
   *   ❌ Admin-only toasts (CMO knowledge, distill-to-lessons). They sit in
   *      user-facing files behind an `isAdmin` gate and carry an explicit
   *      eslint-disable at the call site marking them as intentional.
   *   ❌ `res.error` strings returned by server actions. Those are produced
   *      server-side and are a separate surface; the call sites here only
   *      translate the FALLBACK used when the server returns no message.
   */
  toast: {
    // Folders.
    folderCreated: 'Folder created',
    folderCreateFailed: 'Failed to create folder',
    folderRenamed: 'Folder renamed',
    folderRenameFailed: 'Failed to rename folder',
    folderDeleted: 'Folder deleted. Its items were kept.',
    folderDeleteFailed: 'Failed to delete folder',
    movedToFolder: 'Moved to folder',
    removedFromFolder: 'Removed from folder',
    moveFailed: 'Failed to move',
    removeFailed: 'Failed to remove',
    /** Count of items added to a folder in one go. */
    itemsAdded: '{count, plural, one {Added # item} other {Added # items}}',

    // Transcripts.
    fileRenamed: 'File renamed',
    fileRenameFailed: 'Rename failed',
    transcriptDeleted: 'Transcript deleted.',
    transcriptDeleteFailed: 'Could not delete this transcript. Please try again.',

    // Clipboard and crawling.
    markdownCopied: 'Markdown copied',
    copyBlocked: 'Could not copy. Your browser blocked the clipboard.',
    retryingCrawl: 'Retrying crawl...',
    retryFailed: 'Failed to retry',

    // Support.
    ticketRateLimited: 'You have raised a lot of tickets. Try again shortly.',
    ticketCreateFailed: 'Could not create the ticket. Please try again.',
    ticketClosed: 'Ticket closed.',
    ticketCloseFailed: 'Could not close the ticket.',
    sendFailed: 'Could not send. Please try again.',
    networkError: 'Network error. Please try again.',

    // Billing.
    planCanceledUntil: 'Plan canceled. You keep access until {date}.',
    planCanceledPeriodEnd: 'Plan canceled. You keep access until the period ends.',
    planResumed: 'Plan resumed. It will keep renewing.',
  },

  /**
   * Auth screens — but only the parts we own.
   *
   * Everything inside the card (field labels, buttons, errors, the whole
   * verification flow) belongs to Clerk and comes from `@clerk/localizations`;
   * see `src/i18n/clerk-localization.ts`. What is here is the page around it:
   * the heading, the sub-line, and the cross-link between sign-in and sign-up.
   *
   * These are the first localised words a visitor sees after leaving the
   * marketing pages, and the language has to survive that hop — it comes from
   * the arrival cookie, since there is no account to read a preference from yet.
   */
  auth: {
    signInHeading: 'Welcome back',
    signInSubheading: 'Sign in to your account to continue',
    signInFooterPrompt: 'Don’t have an account?',
    signInFooterAction: 'Sign up',

    signUpHeading: 'Create your account',
    signUpSubheading: 'Get started with Wisprs and transcribe your audio',
    signUpFooterPrompt: 'Already have an account?',
    signUpFooterAction: 'Sign in',
  },

  /**
   * Error and empty states — what a user sees when something has already gone
   * wrong, or before anything has gone right.
   *
   * These matter more than their word count suggests. English here is where a
   * localised product stops feeling localised, because it appears exactly when
   * the user is already unsure whether something is broken.
   *
   * Scope note: this covers the DASHBOARD boundary (`dashboard/error.tsx`), not
   * the root `app/error.tsx` or `app/not-found.tsx`. Those render under the root
   * provider, which is pinned to English by design so it cannot resolve the
   * next-intl request config early (see the comment block in
   * `dashboard/layout.tsx`) — putting these keys there would render English
   * while looking translated, which is worse than leaving it plainly English.
   */
  errors: {
    heading: 'Something went wrong',
    body: 'An unexpected error occurred. Please try again.',
    retry: 'Try again',
    retrying: 'Retrying...',
    backToDashboard: 'Back to dashboard',
  },

  empty: {
    /** Transcript list, before the first upload. */
    transcriptsHeading: 'No transcriptions yet',
    transcriptsBody: 'Get started by uploading your first audio or video file.',
    transcriptsAction: 'Create your first transcription',
    /** Same list, but filtered down to nothing. A different problem. */
    transcriptsFiltered: 'No transcriptions match your filters. Try adjusting your search criteria.',

    foldersHeading: 'No folders yet',
    foldersBody:
      'Create your first folder to organize your transcriptions and files for better management.',
    foldersAction: 'Create Your First Folder',
    foldersFiltered: 'No folders match “{query}”.',
  },

  /**
   * Upgrade and paywall surfaces.
   *
   * These carry PRICES and PRODUCT CLAIMS, which is why they were held back
   * from the first settings/billing pass: I18N_PLAN.md §6 puts claim-bearing
   * strings behind the founder review gate rather than a mechanical extraction.
   * Translated here on an explicit instruction to do so; the claim-bearing ones
   * are `subStudio`, `subPro`, `subTrial`, `subDaily`, `subNextTier` and
   * `trialExhaustedSub`, and they are the short list worth re-reading per
   * locale.
   *
   * ── Prices are DATA, not copy ───────────────────────────────────────────────
   *
   * `{price}` is always interpolated from `pricing-data.ts`, never written into
   * a translation. A number baked into eleven catalogs is eleven places to miss
   * when pricing changes. Each locale decides where the currency symbol goes,
   * because "$25/mo" and "25 $/mois" are both correct and only one is right per
   * language.
   *
   * ── Plan LABELS are split in two on purpose ─────────────────────────────────
   *
   * `upgradeCtaFor()` returns a label that is BOTH rendered and sent to
   * `event_log` as the `plan` dimension of `paywall_cta_clicked`. Translating it
   * in place would push "Obtener Studio de nuevo" into analytics and split the
   * funnel by language. So the English label stays the analytics token and the
   * keys below are what the user actually reads.
   */
  upgrade: {
    // Plan CTA verb phrases. Mirror `upgradeCtaFor()`'s three contexts.
    planKeepStudio: 'Keep Studio',
    planGetStudioBack: 'Get Studio back',
    planUpgradeToPro: 'Upgrade to Pro',
    planUpgradeGeneric: 'Upgrade plan',

    /** Button label with the price appended, e.g. "Upgrade to Pro · $25/mo →". */
    ctaWithPrice: '{label} · ${price}/mo →',
    /** Same, prefixed, for the secondary link under the bonus button. */
    ctaWithPriceSecondary: 'Or {label} · ${price}/mo →',
    /**
     * Annual-first variants (2026-09-01): headline the annual per-month rate,
     * ALWAYS with the billed-annually qualifier — never render the annual
     * figure through the plain templates above.
     */
    ctaWithAnnualPrice: '{label} · ${price}/mo billed annually →',
    ctaWithAnnualPriceSecondary: 'Or {label} · ${price}/mo billed annually →',
    /** The month-to-month escape under an annual-first CTA. */
    orMonthlyPrice: 'or ${price}/mo month-to-month',
    /**
     * Pay-as-you-go pack offer on the paywall (TOS-52): the smallest pack that
     * lets this one file through, for people who don't want a subscription.
     */
    packCta: 'Buy {minutes} minutes · ${price} one time →',
    packCtaSecondary: 'Or buy {minutes} minutes · ${price} one time →',
    packNote: 'Enough for this file. No subscription, minutes last 12 months.',
    packOpening: 'Opening checkout…',
    packAlreadySubscribed: 'Your plan already includes monthly minutes, so packs are not needed.',
    ctaGeneric: 'Upgrade plan →',

    // Quota banner, free daily cap.
    dailyCapUsed: 'You have used today’s free minutes.',
    dailyMinutesLeft: '{count, plural, one {# free minute left today.} other {# free minutes left today.}}',
    /** Second sentence after either of the two above. Split so the plural
     *  branch does not have to repeat the whole CTA sentence in every language. */
    removeDailyCapPro: 'Upgrade to Pro to remove the daily cap.',
    removeDailyCapStudio: 'Get Studio back to remove the daily cap.',

    // Quota banner, paid monthly cap. `{plan}` is a plan NAME, not translated.
    monthlyCapUsed: 'You have used your monthly minutes on {plan}.',
    monthlyMinutesLeft:
      '{count, plural, one {# minute left this month on {plan}.} other {# minutes left this month on {plan}.}}',

    // Value lines under each CTA. Claim-bearing.
    subStudio: 'Premium accuracy · AI summaries · premium voices · no watermark',
    subStudioShort: 'Premium accuracy · AI summaries · premium voices',
    subPro: '1,000 minutes/month · summaries + exports · no waiting',
    subProShort: '1,000 min/month · no daily cap · summaries + exports',
    subNextTier: 'Next tier unlocks more minutes immediately',

    /** Prefix before the live countdown in the banner. */
    resetsIn: 'Resets in',

    /**
     * Countdown units. These sat outside the catalog and rendered "4h 44m"
     * INSIDE an otherwise fully translated sentence, which is exactly the mixed
     * -language failure this phase exists to avoid. Each locale places and names
     * its own units.
     */
    durationHoursMinutes: '{hours}h {minutes}m',
    durationHours: '{hours}h',
    durationMinutes: '{minutes}m',
    durationSeconds: '{seconds}s',

    // Upgrade-offer modal chrome.
    offerAriaLabel: 'Upgrade offer',
    fileVsCap: 'File: {fileMinutes} min · Free tier cap: {capMinutes} min',
    freeCapResetsIn: 'Free cap resets in',
    comeBackOrUpgrade: 'come back then or upgrade now.',
    claimBonus: '🎁 Transcribe this one free (welcome bonus)',
    claimingBonus: 'Granting bonus...',
    claimBonusFailed: 'Could not claim bonus',
    bonusFootnote:
      'One-time per-account bonus. After this, files past the cap need a Pro plan.',
  },

  /**
   * Paywall prose built SERVER-SIDE by the quota gate.
   *
   * Separate from `upgrade.*` because of where it runs, not what it says: this
   * is produced by `over-cap-copy.ts` and `gate.ts`, which execute in a pg-boss
   * worker as well as in a server action, and therefore reach the catalog
   * through `src/i18n/standalone.ts` rather than through a request.
   *
   * The daily/per-file branching here is load-bearing and documented at length
   * in `over-cap-copy.ts`: a file LONGER than the cap can never fit, so its copy
   * must not offer a countdown. Keep that distinction in every translation. A
   * locale that tells the user to wait for a reset that cannot help recreates
   * the incident that module exists to prevent.
   */
  paywall: {
    /** "{plan}" throughout is this, already priced: "Upgrade to Pro ($25/mo)". */
    planWithPrice: '{label} (${price}/mo)',

    overCapHeadline:
      'This {fileMinutes} min file is longer than the {capMinutes} min free limit',
    overCapWhyDaily:
      'Waiting for the reset will not help: the free plan allows {capMinutes} min per day, so a {fileMinutes} min file will not fit in one.',
    overCapWhyPerFile:
      'Waiting will not help: the free plan handles files up to {capMinutes} min, and this one is {fileMinutes} min.',
    overCapSub: '{why} {plan} transcribes it in a single pass.',
    overCapReason:
      'This {fileMinutes} min file is longer than the {capMinutes} min free limit. Waiting will not help. {plan} transcribes it in one pass.',
    overCapBonusSub:
      'We can transcribe this one free as a welcome. After that, files over {capMinutes} min need {plan}, which removes the limit.',
    overCapBonusReason:
      'This {fileMinutes} min file is over the {capMinutes} min free limit. We can transcribe this one free as a welcome.',

    dailyLimitHeadline: 'Daily free limit reached ({capMinutes} min)',
    dailyLimitSub:
      'Your {fileMinutes} min file fits the free limit, there just is not enough left today.{resets} {plan} removes the wait.',
    dailyLimitReason:
      'You have used your {capMinutes} free minutes for today.{resets} {plan} removes the daily cap.',
    /** Injected into the two above. Leading space is intentional. */
    resetsSentence: ' Resets in {countdown}.',

    trialExhaustedReason:
      'Your trial’s premium minutes are used up ({remaining} left, this file is {fileMinutes} min). {plan} to keep premium accuracy, or continue on the Free plan.',
    trialExhaustedHeadline: 'Trial premium minutes used up',
    trialExhaustedSub:
      'You have used your trial’s premium STT minutes. {label} to keep ElevenLabs-grade accuracy, AI summaries and premium voices, or keep going free.',

    monthlyCapHeadline: 'Monthly minutes used ({used}/{limit})',
    monthlyCapSub: 'Upgrade to the next plan to keep transcribing this billing cycle.',
    monthlyCapReason:
      'You have used your monthly {limit} minutes ({remaining} left). Upgrade to keep transcribing this month.',
  },

  /** The settings index and its section cards. */
  settings: {
    heading: 'Settings',
    subheading: 'Manage your account settings and preferences',

    modeTitle: 'Dashboard Mode',
    modeDescription: 'Choose which features and navigation items are visible in your dashboard',
    modeCurrentlyActive: 'Currently active',
    appearanceTitle: 'Appearance',
    appearanceDescription: 'Choose how Wisprs looks on this device.',
    appearanceSystem: 'System',
    appearanceLight: 'Light',
    appearanceDark: 'Dark',
    appearanceSystemDescription: 'System follows your device setting.',

    billingTitle: 'Billing & Usage',
    billingDescription: 'Manage your subscription, view usage details, and access billing history',
    billingAction: 'Manage Billing',

    supportTitle: 'Support',
    supportDescription:
      'Raise a support ticket and track it to resolution. We reply by email and in-app.',
    supportAction: 'Support tickets',

    accountTitle: 'Account',
    accountDescription: 'Your account information and profile settings',
    accountEmail: 'Email',
    accountUserId: 'User ID',

    preferencesTitle: 'Preferences',
    preferencesDescription: 'Customize your experience and notification settings',
    preferencesMarketingTitle: 'Product and lifecycle emails',
    preferencesMarketingDescription: 'Get tips, product updates, and occasional reminders that help you get more from Wisprs.',
    preferencesTransactionalNote: 'Service emails, such as transcription completion and billing notices, stay on.',
    preferencesSaved: 'Preference saved',
    preferencesSaveFailed: 'Could not save this preference. Try again.',

    apiTitle: 'API & Integrations',
    apiDescription: 'Manage API keys, webhooks, and third-party integrations',
    apiAction: 'Manage API',

    privacyTitle: 'Data & Privacy',
    privacyDescription: 'Control your data retention, export, and account deletion',
  },

  /**
   * Billing and usage.
   *
   * Money-adjacent, so the fallback-to-English accessor matters more here than
   * anywhere else in the app: a raw catalog key on a cancellation dialog reads
   * as a broken payment screen.
   *
   * Currency amounts, plan names and order IDs are NOT here. They are data, and
   * `toLocaleDateString`/`toLocaleString` already format dates and numbers per
   * the user's own locale.
   */
  billing: {
    heading: 'Billing & Usage',
    subheading: 'Manage your subscription and view usage details',
    loadFailed: 'Failed to load billing information',

    currentPlan: 'Current Plan',
    renewsOn: 'Renews on {date}',
    cancelsOn: 'Cancels on {date}',

    meterTranscription: 'Transcription Minutes',
    meterTextToSpeech: 'Text-to-speech',
    meterTranslation: 'Translation',
    /** Shown in place of a number on an uncapped plan. */
    unlimited: 'Unlimited',
    /** Unit after a meter reading, e.g. "12.0 / 30 minutes". */
    unitMinutes: 'minutes',
    /** Free tier meters a DAILY cap, so the unit says so. */
    unitMinutesToday: 'minutes today',
    unitCharacters: 'characters',

    historyTitle: 'Billing History',
    historyEmpty: 'No billing history found',
    historyDate: 'Date',
    historyAmount: 'Amount',
    historyStatus: 'Status',
    historyOrderId: 'Order ID',

    upgradeAction: 'Upgrade Plan',
    manageAction: 'Manage billing',
    resumeAction: 'Resume plan',
    cancelAction: 'Cancel plan',
    working: 'Working...',
    canceling: 'Canceling...',

    cancelConfirmTitle: 'Cancel your plan?',
    cancelConfirmUntil:
      'Your plan stays active until {date}. You keep full access until then, with no further charges after this period. You can resume anytime before it ends.',
    cancelConfirmPeriodEnd:
      'Your plan stays active until the end of the current paid period. You keep full access until then and will not be charged again. You can resume anytime before it ends.',
    cancelConfirmKeep: 'Keep my plan',
    cancelConfirmYes: 'Yes, cancel',
    close: 'Close',
  },

  /** Language preference, surfaced in settings. */
  language: {
    heading: 'Language',
    description: 'The language of the Wisprs interface. Transcription supports 100+ languages regardless of this setting.',
    saved: 'Language updated',
    saveFailed: 'Could not update language',
  },

  home: {
    welcomeBack: 'Welcome back, {name}.',
    tagline: 'Turn a recording into useful text.',
    startTranscription: 'Start a transcription',
    statTotalTranscripts: 'Total transcripts',
    statRecent30Days: '+{count} in the last 30 days',
    statAllTime: 'All time',
    statMinutesTranscribed: 'Minutes transcribed',
    statTranscribed: 'Transcribed',
    statActiveJobs: 'Active jobs',
    statCurrentlyProcessing: 'Currently processing',
    statCompletedThisMonth: 'Completed this month',
    statComparedLastMonth: 'Compared with last month',
    greetingNamed: 'Hi {name}, let\'s get your first transcript.',
    greeting: 'Let\'s get your first transcript.',
    emptyIntro: 'Drop in any audio or video file and Wisprs will return a clean transcript, an AI summary, chapters with timestamps, action items, and export-ready captions, usually in under 90 seconds.',
    emptyCta: 'Transcribe my first file →',
    emptyUrlHint: 'Or paste a YouTube / Drive / podcast URL after upload.',
    benefitTranscriptTitle: 'Transcript + speakers',
    benefitTranscriptBody: 'Clean, speaker-labeled text with timestamps. 100+ languages.',
    benefitSummaryTitle: 'AI summary + chapters',
    benefitSummaryBody: 'One-click summary, auto-chaptered for show notes.',
    benefitExportTitle: 'Export anywhere',
    benefitExportBody: 'TXT, SRT, VTT, DOCX, MD. Burn-ready captions included.',
    onboardingDismiss: 'Dismiss',
    onboardingProgress: '{completed} of {total} done',
    onboardingStepIntentLabel: 'What are you transcribing?',
    onboardingStepIntentHint: 'So we can tune what we show you.',
    onboardingStepFirstUploadLabel: 'Add your first file',
    onboardingStepFirstUploadHint: 'Upload, paste a link, or try a sample.',
    onboardingStepFirstTranscriptLabel: 'Get your transcript',
    onboardingStepFirstTranscriptHint: 'We will email you when it is ready.',
    onboardingIntentPodcast: 'Podcasts',
    onboardingIntentMeetings: 'Meetings',
    onboardingIntentInterviews: 'Interviews',
    onboardingIntentLectures: 'Lectures',
    onboardingIntentVideos: 'Videos',
    onboardingIntentOther: 'Something else',
    onboardingUploadTab: 'Upload a file',
    onboardingLinkTab: 'Paste a link',
    onboardingChooseFile: 'Choose a file →',
    onboardingLinkLabel: 'Audio or video link',
    onboardingLinkPlaceholder: 'Paste a YouTube, Loom, Vimeo, TikTok, podcast or media file link',
    onboardingLinkStarting: 'Starting…',
    onboardingLinkSubmit: 'Transcribe link →',
    onboardingLinkHint: 'Any public link to a video or audio file.',
    recentHeading: 'Recent work',
    recentSubheading: 'Open a transcript or start a new one.',
    recentViewAll: 'View all',
    recentEmptyTitle: 'No transcripts yet',
    recentEmptyBody: 'Upload a recording and it will show up here.',
    recentEmptyAction: 'Start your first transcription',
  },

  library: {
    description: 'Find, open, and organize your transcripts.',
    descriptionWithCount: '{count, plural, one {# transcript. Find, open, and organize it.} other {# transcripts. Find, open, and organize them.}}',
    loading: 'Loading transcripts',
    loadFailed: 'We could not load your transcripts. Try again.',
    loadFailedOffline: 'We could not load your transcripts. Check your connection and try again.',
    manageFolders: 'Manage folders',
    selectedCount: '{count} selected',
    bulkExport: 'Export',
    moveToFolder: 'Move to folder',
    clearSelection: 'Clear',
    showingRange: 'Showing {start}-{end} of {total}',
    loadMore: 'Load more transcripts',
    deleteDialogTitle: 'Delete transcript?',
    deleteDialogBody: 'This removes the transcript and its retained media. This action cannot be undone.',
    deleteDialogConfirm: 'Delete transcript',
    cancel: 'Cancel',
    columnFile: 'File',
    columnFileName: 'File name',
    columnStatus: 'Status',
    columnDate: 'Date',
    columnDuration: 'Duration',
    columnFileSize: 'File size',
    columnLanguage: 'Language',
    columnActions: 'Actions',
    sortByFileName: 'Sort by file name',
    sortByStatus: 'Sort by status',
    sortByDate: 'Sort by date',
    sortByDuration: 'Sort by duration',
    sortByFileSize: 'Sort by file size',
    sortByLanguage: 'Sort by language',
    selectColumn: 'Select',
    selectItem: 'Select {name}',
    meetingBadge: 'Meeting',
    durationUnknown: 'Unknown',
    languageAutoDetected: 'Auto-detected',
    openTranscript: 'Open transcript',
    open: 'Open',
    deleteTranscription: 'Delete transcription',
    noTranscriptsToShow: 'No transcripts to show.',
    statsTranscriptsInView: 'Transcripts in view',
    statsStatusInView: 'Status in view',
    statsLoadedRecords: 'Loaded records',
    statsTopLanguage: 'Top language',
    statsNotAvailable: 'N/A',
    statsMostTranscribed: 'Most transcribed',
    statsAvgProcessingTime: 'Avg processing time',
    statsLoadedCompletedRecords: 'Loaded completed records',
    channelsTitle: 'Channels',
    channelsDescription: 'Everything you have said across your back catalog, searchable. Find the moment, then repurpose it.',
    searchTitle: 'Search',
    repurposeDescription: 'Pick episodes and turn them into a blog post, thread or show notes.',
    proGateBody: 'Searchable library is a Pro feature. Upgrade to sync your whole channel and search every word you have said.',
    proGateAction: 'Upgrade',
    syncedChannelsTitle: 'Synced channels',
    syncChannelAction: 'Sync a YouTube channel',
    noChannelsTitle: 'No channels synced yet',
    noChannelsBody: 'Sync a YouTube channel to search and repurpose its back catalog.',
    syncStatusIdle: 'Idle',
    syncStatusListing: 'Listing videos',
    syncStatusSyncing: 'Syncing',
    syncStatusComplete: 'Synced',
    syncStatusFailed: 'Failed',
    channelProgressDone: '{completed}/{total} done',
    channelInProgress: '{count} in progress',
    channelFailed: '{count} failed',
    etaLeft: '~{duration} left',
  },

  transcribe: {
    pageTitle: 'Transcribe audio, video, or a link',
    pageDescription: 'Upload a file or paste a public media link. You can add more than one file.',
    cancelFailed: 'Failed to cancel',
    unitBytes: 'Bytes',
    fileTooLarge: 'This file is too large to upload (max {size}). Compress or export a smaller file and try again.',
    fileTypeUnsupported: 'File type not supported. Supported formats: {formats}',
    linkStartFailed: 'Could not start that link. Please try again.',
    seePlans: 'See plans',
    dismiss: 'Dismiss',
    sourceLabel: 'Source',
    sourceAriaLabel: 'Transcription source',
    sourceFile: 'File',
    sourceLink: 'Link',
    fileHelper: 'Upload audio or video from your device.',
    linkHelper: 'Paste one public HTTPS media link. We download it securely and apply your plan limits.',
    dropTitleActive: 'Add more files anytime',
    dropTitle: 'Drop audio or video here',
    dropBodyActive: 'Progress for each file is shown below. You can cancel an upload in progress.',
    dropBody: 'Drag and drop files here or click to browse',
    addFiles: 'Add files',
    chooseFiles: 'Choose files',
    supportedFormats: 'Supported formats',
    linkTitle: 'Transcribe from a link',
    linkBody: 'Paste one public HTTPS link to a media file or YouTube video. We download it in the background, scan the bytes as media, and send it through your normal plan allowance.',
    linkInputLabel: 'Audio or video link',
    linkPlaceholder: 'https://youtube.com/watch?v=… or https://cdn.example.com/audio.mp3',
    startTranscription: 'Start transcription',
    linkSafetyNote: 'Public HTTPS only. Local addresses, redirects to private networks, HTML pages, and executable files are blocked.',
    filesHeading: 'Files ({count})',
    clearAll: 'Clear all',
    statusQueued: 'Queued',
    statusAssembling: 'Assembling file…',
    statusStartingUpload: 'Starting upload…',
    statusUploading: 'Uploading…',
    statusUploadComplete: 'Upload complete',
    stageRouting: 'Selecting transcription provider…',
    stageConnecting: 'Connecting to {provider}…',
    stageTranscribingWith: 'Transcribing with {provider}…',
    stageLocalStarting: 'Starting local transcription…',
    stageLocalProcessing: 'Processing audio locally…',
    stageFinalizing: 'Finalizing transcript…',
    stageTranscribingLocally: 'Transcribing locally…',
    statusTranscribing: 'Transcribing…',
    statusDone: 'Done',
    statusFailed: 'Failed',
    statusProcessing: 'Processing',
    authRequired: 'Authentication required. Please log in and try again.',
    cancel: 'Cancel',
    cancelling: 'Cancelling…',
    etaEstimating: 'Estimating ETA…',
    etaPrefix: 'ETA:',
    etaOnFree: 'on Free',
    etaOnPro: 'on Pro',
    retranscribeFailed: 'Failed to retranscribe: {error}',
    unknownError: 'Unknown error',
    reusedTitle: 'Reused from Previous Upload',
    reusedBadge: 'Cached',
    reusedBody: 'We found a previously completed transcription for this file and reused it.',
    retranscribing: 'Retranscribing...',
    retranscribe: 'Retranscribe',
    readyTitle: 'Ready to transcribe',
    readyBody: 'Your file is uploaded. Choose a mode and start transcription.',
    modeLabel: 'Transcription mode',
    modeAutoRecommended: 'Auto (recommended)',
    modeSpeed: 'Fast draft',
    modeQuality: 'Best quality',
    modeHelpPaid: 'Fast draft uses a faster model without speaker labels. Best quality keeps speaker labels.',
    modeHelpFree: 'Auto balances speed and quality. Large files may use the fast model to stay reliable.',
    starting: 'Starting...',
    preparingTitle: 'Preparing your media',
    preparingBody: 'Downloading and checking your link securely… Transcription will start automatically.',
    meetingTitle: 'Meeting transcription will start automatically',
    meetingBadge: 'Meeting',
    meetingBody: 'Wisprs is saving the meeting recording and will transcribe it as soon as the uploaded audio is finalized.',
    linkedTitle: 'Linked to Existing Transcription',
    processingTitle: 'Processing your transcription',
    processingBody: 'This may take a few minutes. You can leave this page; we\'ll notify you when it\'s ready.',
    etaCompleteIn: 'Estimated complete in',
    etaProFinishesIn: 'Pro finishes in',
    statusOffline: 'Offline. Will resume when you reconnect.',
    resumeHeading: 'Unfinished uploads',
    resumeInterrupted: 'This upload was interrupted. Choose the same file to continue where it stopped.',
    resumeChooseFile: 'Choose file',
    resumeDiscard: 'Discard',
    resumeWrongFile: 'That is a different file. Choose {fileName} to continue.',
    resumeDone: 'This upload finished while you were away.',
    resumeOpenTranscript: 'Open transcript',
  },

  transcript: {
    createDescSummary: 'A concise recap of the key ideas and context.',
    createDescActions: 'Turn the conversation into clear next steps.',
    createDescTopics: 'Group the transcript by its main themes.',
    createDescChapters: 'Break a long recording into easy sections.',
    createDescMinutes: 'Create structured notes you can share.',
    createDescSpeakers: 'See what each speaker contributed.',
    createDescSalesCallKit: 'Build a follow-up email and CRM note.',
    createDescFallback: 'Open this output in the workspace.',
    createHintShort: 'Short recording: a summary or action list gets to the point fastest.',
    createHintMedium: 'A summary, action points and topics work well at this length.',
    createHintLong: 'Long recording: meeting minutes and chapters make it easy to navigate.',
    createAriaLabel: 'Create from this transcript',
    createHeading: 'Turn your transcript into something useful',
    createIntro: 'Choose an output to open it or generate it. You can review the result before using it.',
    recommendedOutputs: 'Recommended for this transcript',
    moreOutputs: 'More outputs',
    translateTitle: 'Translate transcript',
    translateDescription: 'Create a version in another language.',
    workspaceAriaLabel: 'Transcript workspace',
    workspaceAsk: 'Ask',
    workspaceCreate: 'Create',
    allOutputs: 'All outputs',
    searchAriaLabel: 'Search transcript',
    searchPlaceholder: 'Search in transcript...',
    edit: 'Edit',
    doneEditing: 'Done editing',
    replacementAriaLabel: 'Replacement text',
    replacePlaceholder: 'Replace with...',
    saving: 'Saving…',
    reviewReplaceAll: 'Review replace all',
    searchNotFound: '“{query}” not found',
    replaceConfirmQuestion: '{count, plural, one {Replace # match of “{query}” with “{replacement}”?} other {Replace all # matches of “{query}” with “{replacement}”?}}',
    replaceConfirmNote: 'This saves your transcript. You can undo this change.',
    replacedSaved: 'Replaced & saved',
    saveFailed: 'Failed to save',
    changeUndone: 'Change undone',
    undoFailed: 'Could not undo. Please try again.',
    undoLastReplacement: 'Undo last replacement',
    checkingAccess: 'Checking access…',
    viewOptions: 'View options',
    timestamps: 'Timestamps',
    speakerNames: 'Speaker names',
    speakerNamesUnavailable: 'Speaker names are not available for this transcript. Use a transcription mode with speaker detection to add them.',
    noTranscriptText: 'No transcript text available.',
    loadSummaryFailed: 'Failed to load summary',
    generateChaptersFailed: 'Failed to generate chapters',
    loadChaptersFailed: 'Failed to load chapters',
    generateMinutesFailed: 'Failed to generate minutes',
    loadMinutesFailed: 'Failed to load minutes',
    extractTopicsFailed: 'Failed to extract topics',
    loadTopicsFailed: 'Failed to load topics',
    diarizationUnavailable: 'Speaker diarization not available',
    generateSpeakersFailed: 'Failed to generate speaker summary',
    loadSpeakersFailed: 'Failed to load speaker summary',
    loadActionPointsFailed: 'Failed to load action points',
    loadSalesCallKitFailed: 'Failed to load sales call kit',
    loadingSummary: 'wisprn\' the summary...',
    loadingChapters: 'wisprn\' the chapters...',
    loadingMinutes: 'wisprn\' the minutes...',
    loadingSpeakers: 'wisprn\' the speakers...',
    loadingTopics: 'wisprn\' the topics...',
    retry: 'Retry',
    regenerateChapters: 'Regenerate chapters',
    regenerateMinutes: 'Regenerate minutes',
    regenerateSpeakers: 'Regenerate speaker summary',
    regenerateTopics: 'Regenerate topics',
    chaptersEmpty: 'No chapters identified yet. Click to generate chapters.',
    chaptersCount: '{count, plural, one {# chapter identified} other {# chapters identified}}',
    meetingMinutes: 'Meeting Minutes',
    minutesEmpty: 'No minutes available. Click to generate minutes.',
    date: 'Date',
    duration: 'Duration',
    attendees: 'Attendees',
    agenda: 'Agenda',
    keyDecisions: 'Key Decisions',
    actionItems: 'Action Items',
    owner: 'Owner: {owner}',
    due: 'Due: {date}',
    speakersEmpty: 'No speaker diarization data available',
    speakersEmptyHint: 'This transcript has no speaker labels. Try transcription again to add speaker labels (Pro and above).',
    speakersCount: '{count, plural, one {# speaker identified} other {# speakers identified}}',
    percentOfTotal: '{percent}% of total',
    keyPointsLabel: 'Key Points:',
    topicsLabel: 'Topics:',
    contributionsLabel: 'Contributions:',
    topicsEmpty: 'No topics identified yet. Click to extract topics.',
    topicsCount: '{count, plural, one {# topic identified} other {# topics identified}}',
    mentionsCount: '{count, plural, one {# mention} other {# mentions}}',
    topicDuration: '{time} duration',
    keyInsightsLabel: 'Key Insights:',
    noSummary: 'No summary available',
    wordCount: 'Word Count',
    characters: 'Characters',
    speakers: 'Speakers',
    sentiment: 'Sentiment',
    sentimentPositive: 'positive',
    sentimentNegative: 'negative',
    sentimentNeutral: 'neutral',
    timelineHighlights: 'Timeline Highlights',
    noTimeline: 'No timeline events available',
    topics: 'Topics',
    noTopics: 'No topics available',
    keyPoints: 'Key Points',
    noKeyPoints: 'No key points available',
    speakerInsights: 'Speaker Insights',
    aiFeatures: 'AI features',
    unlockFeature: 'Unlock {feature}',
    paywallBody: 'AI summaries, chat, action points, chapters and minutes, generated instantly from any transcript.',
    paywallTrialSub: 'Free for 7 days on Studio · premium accuracy · AI summaries · no card',
    paywallGetStudio: 'Get Studio · ${price}/mo →',
    starting: 'Starting…',
    paywallStartTrial: 'Unlock AI free for 7 days, no card →',
    paywallNoCard: 'No card required · reverts to Free after 7 days',
    actionsAriaLabel: 'Transcript actions',
    copy: 'Copy',
    export: 'Export',
    share: 'Share',
    downloadAudio: 'Download audio',
    openInAiToolAriaLabel: 'Open in another AI tool',
    openIn: 'Open in',
    openInTool: 'Open in {tool}',
    openInToolTooltip: 'Open in {tool}. You review the handoff first.',
    more: 'More',
    useAnotherAiTool: 'Use another AI tool',
    reviewHandoff: 'Review the handoff before sharing your transcript.',
    deleteTranscript: 'Delete transcript',
    exportTitle: 'Export transcript',
    fileFormat: 'File format',
    formatTxt: 'Plain text (.txt)',
    formatDocx: 'Word document (.docx)',
    formatSrt: 'Subtitles (.srt)',
    formatVtt: 'Web subtitles (.vtt)',
    formatJson: 'Structured data (.json)',
    exportFormatAriaLabel: 'Transcript export format',
    captionLength: 'Caption length',
    captionFullSentences: 'Full sentences',
    captionWords: '{count, plural, one {# word per caption} other {# words per caption}}',
    includeTimestamps: 'Include timestamps',
    includeSpeakerNames: 'Include speaker names',
    preparingDownload: 'Preparing download…',
    downloadTranscript: 'Download transcript',
    viewPlans: 'View plans',
    exportDownloaded: 'Export downloaded.',
    exportFailed: 'Could not export. Please try again.',
    transcriptCopied: 'Transcript copied.',
    copyFailed: 'Could not copy. Select the text and copy it manually.',
    deleteFailed: 'Could not delete. Please try again.',
    deleteConfirmTitle: 'Delete transcript?',
    deleteConfirmBody: 'This removes the transcript and its retained media. This action cannot be undone.',
    cancel: 'Cancel',
    statusReady: 'Ready',
    statusPending: 'Pending',
    statusProcessing: 'Processing',
    statusWaiting: 'Waiting',
    statusCancellationRequested: 'Cancellation requested',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
    fileHistory: 'File history',
    created: 'Created',
    completed: 'Completed',
    cachedFoundTitle: 'Cached Transcription Found',
    cachedFromPreviousUpload: 'From Previous Upload',
    cachedFoundBody: 'We found a previously completed transcription for this file.',
    failedTitle: 'Transcription Failed',
    failedWithCache: 'This transcription failed, but we found a cached version above. You can transcribe afresh if needed.',
    tryAgain: 'Try transcription again',
    durationSeconds: '{seconds} sec',
    durationMinutes: '{minutes} min',
    durationHoursMinutes: '{hours} h {minutes} min',
    loadingTitle: 'Loading transcript',
    loadingDescription: 'Opening your transcript and media controls.',
  },

  accountSettings: {
    backToSettings: 'Back to settings',
    billingLoadingDescription: 'Your plan details will appear here.',
    billingLoadingTitle: 'Loading plan and usage',
    cancel: 'Cancel',
    changeMind: 'Need to change your mind? Reply on the ticket before deletion is confirmed.',
    confirmationError: 'Type {phrase} exactly and confirm that you understand.',
    consequenceBilling: 'Active subscriptions and unpaid balances may need to be settled first.',
    consequenceContent: 'Saved transcripts, uploads, generated outputs, and account data may be removed.',
    consequenceRetention: 'Limited records may remain in backups or where we must keep them by law.',
    deleteDescription: 'We will confirm the request and handle billing, data, and backup retention safely.',
    deletePageDescription: 'Review what happens to your content and submit a verified request for our team to process.',
    deleteTitle: 'Request account deletion',
    exportDescription: 'Download transcripts and generated outputs in the formats your plan supports.',
    exportTitle: 'Export your content',
    keepAccount: 'Keep my account',
    modeSwitchBody: 'Your dashboard will reload with the new layout. Navigation items and features will change based on the selected mode.',
    modeSwitchCreatorBody: 'Your dashboard will reload with the new workflow. Creator Mode changes navigation, dashboard sections, and episode actions to focus on intake, repurposing, and export.',
    modeSwitchSubtitle: 'This will change your dashboard layout',
    modeSwitchTitle: 'Switch Dashboard Mode',
    networkError: 'We could not reach Wisprs. Check your connection and try again.',
    optional: 'Optional',
    phraseHelp: 'We use this phrase to prevent accidental requests.',
    privacyNoteAfter: '.',
    privacyNoteBefore: 'Learn what we store and how deletion works in our',
    privacyNoteLink: 'Privacy Policy',
    reasonLabel: 'Anything we should know?',
    reasonPlaceholder: 'Tell us why you are leaving, if you would like to.',
    requestFailed: 'We could not start the request. Please try again or contact support.',
    requestOnFileBody: 'Ticket {ref} is {status}. Your account and content stay available until the request is reviewed and any required confirmation is complete.',
    requestOnFileTitle: 'Your request is on file',
    reviewBody: 'This starts a verified deletion review. It does not delete your account immediately. We will confirm the scope, billing impact, and any retention required by law before permanent removal.',
    reviewTitle: 'Review before you request deletion',
    sendingRequest: 'Sending request...',
    settingsLoadingDescription: 'Your preferences will appear here.',
    settingsLoadingTitle: 'Loading settings',
    startRequest: 'Start deletion request',
    statusAwaitingReview: 'awaiting review',
    statusClosed: 'closed',
    statusReceived: 'received',
    statusReplyAvailable: 'answered',
    switchMode: 'Switch Mode',
    switching: 'Switching...',
    switchingFrom: 'Switching from:',
    switchingTo: 'Switching to:',
    tooManyRequests: 'Too many requests. Please try again later.',
    typePhrase: 'Type {phrase}',
    understandCheckbox: 'I understand that this starts a permanent account deletion review.',
    viewDeletionRequest: 'View deletion request',
  },

  actionPoints: {
    assignedTo: 'Assigned to: {assignee}',
    completedOfTotal: '{completed} of {total} completed',
    due: 'Due: {date}',
    empty: 'No action points extracted from this transcript.',
    exportCompleted: 'Completed: {value}',
    exportHeading: 'Action Points Summary',
    exportPending: 'Pending: {value}',
    exportTotal: 'Total: {value}',
    loading: 'wisprn\' the action points...',
    regenerate: 'Regenerate action points',
    title: 'Action Points',
  },

  apiSettings: {
    activeKeysDescription: 'Revoke keys you no longer need. Rotate by creating a replacement, moving traffic, then revoking.',
    activeKeysTitle: 'Active Keys',
    allKeys: 'All keys',
    anyKey: 'Any key',
    anySpeed: 'Any speed',
    apiReference: 'API reference',
    apply: 'Apply',
    attachToKeyAria: 'Attach to API key',
    attachToKeyOptional: 'Attach to API key (optional)',
    chartEmpty: 'No requests in the last 30 days.',
    chartTitle: 'Requests: last 30 days',
    colAudioMinutes: 'Audio (min)',
    colExports: 'Exports',
    colLatency: 'Latency',
    colMethod: 'Method',
    colMonth: 'Month',
    colPath: 'Path',
    colRequests: 'Requests',
    colStatus: 'Status',
    colTime: 'Time',
    configuredEndpoints: 'Configured endpoints',
    copyKeyNow: 'Copy this key now',
    createKey: 'Create key',
    createKeyDescription: 'Keys are shown once. Store them securely and scope them tightly.',
    createKeyTitle: 'Create API Key',
    creating: 'Creating…',
    delivered: 'Delivered',
    deliveryDescription: 'Recent webhook delivery attempts with response details.',
    deliveryError: 'error',
    deliveryTitle: 'Delivery History',
    description: 'Manage API keys, inspect usage and configure webhook callbacks.',
    endpointActive: 'Active',
    endpointPaused: 'Paused',
    entriesCount: '{count, plural, one {# entry} other {# entries}}',
    expiresInDays: 'Expires in days (optional)',
    failed: 'Failed',
    filterApiKey: 'API key',
    filterApiKeyAria: 'API key filter',
    filterEndpoint: 'Endpoint',
    filterSince: 'Since',
    filterSlowerThan: 'Slower than',
    keyContextAria: 'API key context',
    keyContextOptional: 'API key context (optional)',
    keyFallbackName: 'Key #{id}',
    keyName: 'Key name',
    keyNamePlaceholder: 'Production ingestion',
    keyNumberName: 'Key {id}',
    lastUsed: 'Last used: {date}',
    latencyFilterAria: 'Latency filter',
    legendErrors: 'Errors',
    legendSuccess: 'Success',
    loadingDescription: 'Your keys and request history will appear here.',
    loadingTitle: 'Loading API settings',
    milliseconds: '{value} ms',
    monthlyBreakdown: 'Monthly breakdown',
    never: 'Never',
    next: 'Next',
    noData: 'No data yet.',
    noDeliveries: 'No deliveries yet.',
    noEndpoints: 'No endpoints yet.',
    noKeyAssociation: 'No key association',
    noKeys: 'No API keys yet.',
    noRequests: 'No API requests logged yet.',
    noRequestsFiltered: 'No requests match these filters.',
    notAvailable: 'n/a',
    pause: 'Pause',
    previous: 'Prev',
    resume: 'Resume',
    retry: 'Retry',
    revoke: 'Revoke',
    saveEndpoint: 'Save webhook endpoint',
    saving: 'Saving…',
    scopes: 'Scopes',
    seconds: '{value} s',
    sendTest: 'Send test webhook',
    sendTestToUrl: 'Send test to URL',
    sending: 'Sending…',
    signingSecret: 'Signing secret',
    signingSecretOptional: 'Signing secret (optional)',
    statAudioMinutes: 'Audio minutes (30d)',
    statExports: 'Exports this month',
    statRequests: 'Requests (30d)',
    statSuccessRate: 'Success rate (30d)',
    statusAll: 'All',
    statusFilterLabel: 'Status:',
    tabKeys: 'Keys & Webhooks',
    tabLogs: 'Request Logs',
    testHttp: 'HTTP: {status}',
    testSignature: 'Signature: {signature}',
    testStatus: 'Status: {status}',
    topEndpoints: 'Top endpoints (30d)',
    webhookUrl: 'Webhook URL',
    webhooksDescription: 'Register callback destinations for completed jobs. Events are HMAC-signed.',
    webhooksTitle: 'Webhook Endpoints',
  },

  bulkExport: {
    buildingZip: 'Building zip…',
    change: 'Change',
    exportButton: 'Export {scope}',
    exportFailed: 'Export failed. Try again.',
    exportNetworkFailed: 'Export failed. Check your connection and try again.',
    exportQueued: 'We’ll notify you when your export is ready.',
    exportReady: 'Your export is ready. The download should start automatically.',
    formatHeading: 'Format',
    formatVtt: 'WebVTT subtitles (.vtt)',
    includeSpeakers: 'Include speaker labels',
    intro: 'Download one zip with your transcripts in the format you choose. Large exports are built in the background. We’ll notify you when the zip is ready.',
    optionsHeading: 'Options',
    scopeAll: 'All completed transcripts',
    scopeAriaLabel: 'Export scope',
    scopeFolder: 'Folder: {name}',
    scopeFolderFallback: 'Folder',
    scopeSelected: '{count, plural, one {# selected transcript} other {# selected transcripts}}',
    scopeUnfiled: 'Unfiled transcripts',
    searchEmpty: 'No completed files or folders match that search.',
    searchLabel: 'Search files or folders',
    searchPlaceholder: 'Search by file or folder name',
    title: 'Export transcripts',
    whatToExport: 'What to export',
  },

  charts: {
    noData: 'No data',
  },

  chat: {
    answerFailed: 'I could not answer that right now. Please try again.',
    externalChatgptDescription: 'Summarize and chat with this transcript',
    externalChooseHow: 'Choose how to chat:',
    externalClaudeDescription: 'Analyze and discuss this transcript',
    externalCopiedAndOpened: 'Copied & Opened!',
    externalCopyFailed: 'Failed to copy: {error}',
    externalCopyFailedManual: 'Failed to copy to clipboard: {error}\n\nPlease copy the prompt manually from the preview below.',
    externalInternalDescription: 'Use our built-in RAG chat with transcript context',
    externalInternalTitle: 'Chat Internally',
    externalOpenFailed: 'Failed to open external app. Please try again.',
    externalOpenIn: 'Open in {provider}',
    externalOpenInDescription: 'Copy prompt to clipboard and open {provider} in a new tab',
    externalPerplexityDescription: 'Research and analyze this transcript',
    externalPromptCopyNote: 'This prompt will be copied to your clipboard when you click “Open in {provider}”',
    externalPromptPreview: 'Prompt Preview',
    externalShowLess: 'Show Less',
    externalShowMore: 'Show More',
    externalTitle: 'Chat with {provider}',
    externalTranscriptUnavailable: 'Transcript text is not available. Please ensure the transcription is completed.',
    inputAriaLabel: 'Question about this transcript',
    inputPlaceholder: 'Ask about the transcript...',
    intro: 'Ask a question about this transcript.',
    planLimitReached: 'Your plan limit was reached. Check your plan or try again after it resets.',
    readingTranscript: 'Reading the transcript',
    send: 'Send',
    statusConnecting: 'Connecting to the transcript',
    statusInterrupted: 'Response interrupted',
    statusStreaming: 'Streaming response',
    stop: 'Stop',
    suggestBuyingSignals: 'What buying signals or objections came up?',
    suggestDecisions: 'List the decisions, owners, and due dates.',
    suggestEpisodeIdeas: 'Turn this conversation into three ideas for the next episode.',
    suggestFollowUp: 'Draft the next follow-up message from this conversation.',
    suggestQuotable: 'Which statements are worth quoting?',
    suggestQuotesThemes: 'Pull out the strongest quotes and themes.',
    suggestRisks: 'What risks or open questions need follow-up?',
    suggestSummarize: 'Summarize this transcript briefly.',
    suggestTopIdeas: 'What are the three most important ideas?',
    suggestedQuestions: 'Suggested questions:',
    writingAnswer: 'Writing the answer',
  },

  checkout: {
    close: 'Close',
    exportFormats: '{count, plural, one {# Export Format} other {# Export Formats}}',
    featureAiSummaries: 'AI Summaries',
    featureApiAccess: 'API Access',
    featureBatchUpload: 'Batch Upload',
    featureCustomVocabulary: 'Custom Vocabulary',
    featureNoWatermarks: 'No Watermarks',
    featureSpeakerDiarization: 'Speaker Diarization',
    featureTeamCollaboration: 'Team Collaboration',
    minutesPerMonth: '{minutes} Minutes/Month',
    processingBody: 'Subscription is being processed. Please refresh the page in a moment.',
    processingTitle: 'Processing Subscription',
    refreshPage: 'Refresh Page',
    startUsing: 'Start Using {plan}',
    unlimitedMinutesPerMonth: 'Unlimited Minutes/Month',
    unlockedFeatures: 'Unlocked Features',
    verificationFailedBody: 'Unable to verify checkout. Please contact support if this persists.',
    verificationFailedTitle: 'Verification Failed',
    verifyFailedRefresh: 'Failed to verify checkout. Please refresh the page.',
    verifying: 'Verifying your subscription...',
    welcomeBody: 'Your subscription is now active. Here’s what you’ve unlocked:',
    welcomeTitle: 'Welcome to {plan}!',
  },

  comeback: {
    claim: 'Claim it',
    details: 'Code {code} on Pro and Studio annual, ends {date}.',
    dismissAriaLabel: 'Dismiss offer',
    headline: '{percent} off your first year.',
  },

  commandPalette: {
    hintAction: 'Action',
    hintGoTo: 'Go to',
    noMatches: 'No matches',
    placeholder: 'Type a page or action',
    searchAriaLabel: 'Search commands',
    switchAppearance: 'Appearance: switch to {theme}',
    title: 'Command palette',
  },

  creator: {
    copied: 'Copied',
    copy: 'Copy',
    episodeLabel: 'Episode',
    generateFromEpisodes: '{count, plural, one {Generate from # episode} other {Generate from # episodes}}',
    generatePack: 'Generate pack',
    generating: 'Generating…',
    homeIntro: 'Bring any source into Wisprs, turn it into publish-ready assets, and keep your episode queue moving without bouncing between downloader, transcript tool, doc, and export flows.',
    homeTitle: 'Ship today’s content.',
    homeTitleNamed: '{name}, ship today’s content.',
    import: 'Import',
    importing: 'Importing…',
    inboxDescription: 'Bring in any source. Wisprs will normalize it into a transcript-ready episode workflow.',
    inboxTitle: 'Inbox',
    intakeChannelHint: 'Sync your whole channel into a searchable library. Your back catalog is transcribed within your plan’s allowance.',
    intakeChannelLabel: 'YouTube channel',
    intakePlaylistHint: 'We’ll queue up to 25 of the most recent videos as separate episodes.',
    intakePlaylistLabel: 'YouTube playlist',
    intakePodcastHint: 'We’ll pull up to 25 of the latest episodes and skip ones already imported.',
    intakePodcastLabel: 'Podcast RSS feed',
    intakeYoutubeHint: 'Paste a single video link. Full length, no 10-minute cap.',
    intakeYoutubeLabel: 'YouTube video',
    librarySearchButton: 'Search',
    librarySearchEmpty: 'No matches yet. Try different words, or sync more of your channel.',
    librarySearchPlaceholder: 'Search everything you’ve ever said…',
    modeBlog: 'Blog post',
    modeChapters: 'Chapters',
    modeQuotes: 'Pull quotes',
    modeShowNotes: 'Show notes',
    modeSummary: 'Summary',
    modeThread: 'Social thread',
    openInRepurpose: 'Open in Repurpose',
    packEmpty: 'No completed episodes yet. Transcribe a source in the Inbox, then come back to generate packs.',
    packPublishDescription: 'Show notes, summary, and chapters.',
    packPublishTitle: 'Podcast Publish Pack',
    packSocialDescription: 'A thread and pull quotes from the episode.',
    packSocialTitle: 'Social Promo Pack',
    packSubtitleDescription: 'SRT, VTT, and transcript exports.',
    packSubtitleTitle: 'Subtitle Pack',
    queueDescription: 'What needs your attention today, from intake through export.',
    queueTitle: 'Production Queue',
    repurposeDescription: 'Start from the output you need to ship instead of hunting through separate tools.',
    repurposeFormatAriaLabel: 'Repurpose format',
    repurposeIntro: 'Select episodes and turn them into a blog, thread, or show notes, across one or many.',
    repurposeTitle: 'Repurpose',
    sourcePlaylistDescription: 'Queue multiple episodes or long-form videos into one backlog.',
    sourcePlaylistTitle: 'Import a playlist',
    sourcePodcastDescription: 'Use Wisprs as the processing hub for RSS-driven episodes.',
    sourcePodcastTitle: 'Connect a podcast feed',
    sourceUploadDescription: 'Start from the file you already have on disk.',
    sourceUploadTitle: 'Upload audio or video',
    sourceYoutubeDescription: 'Bring in a single video: full length, no 10-minute cap.',
    sourceYoutubeTitle: 'Paste a YouTube link',
    turboLabel: 'Turbo: priority processing. Your channel transcribes and indexes first (included on your plan)',
    upgrade: 'Upgrade',
    workflowProgress: '{done}/{total} done',
    workflowTitle: 'Episode workflow',
  },

  dictation: {
    clear: 'Clear',
    connectionError: 'Connection error',
    liveTitle: 'Live Dictation',
    pause: 'Pause',
    placeholder: 'Your transcribed text will appear here...',
    realtimeTitle: 'Real-Time Transcription',
    recording: 'Recording...',
    resume: 'Resume',
    sessionCreateFailed: 'Failed to create real-time session',
    startFailed: 'Failed to start recording',
    startRecording: 'Start Recording',
    stopRecording: 'Stop Recording',
    transcriptLabel: 'Transcript',
  },

  eta: {
    rangeHours: '{low}-{high} hr',
    rangeMinutes: '{low}-{high} min',
    rangeSeconds: '{low}-{high} sec',
  },

  filters: {
    allLanguages: 'All Languages',
    allStatuses: 'All Status',
    allTime: 'All Time',
    allTypes: 'All Types',
    audio: 'Audio',
    byDate: 'Filter by date',
    byFileType: 'Filter by file type',
    byLanguage: 'Filter by language',
    byStatus: 'Filter by status',
    lastDays: '{count, plural, one {Last # day} other {Last # days}}',
    searchAriaLabel: 'Search transcripts',
    searchPlaceholder: 'Search by file name',
    video: 'Video',
    viewGrid: 'Grid',
    viewLabel: 'View',
    viewTable: 'Table',
  },

  folders: {
    add: 'Add',
    addCount: 'Add {number}',
    addMedia: 'Add media',
    addMediaFailed: 'Failed to add media',
    addMediaLoadFailed: 'Failed to load media',
    adding: 'Adding…',
    allFolders: 'All folders',
    backToFolder: 'Back to folder',
    cancel: 'Cancel',
    charCount: '{chars} chars',
    close: 'Close',
    copyMarkdown: 'Copy markdown',
    crawl: {
      crawlUrls: '{count, plural, one {Crawl # URL} other {Crawl # URLs}}',
      crawlUrlsEmpty: 'Crawl URLs',
      createJobFailed: 'Failed to create crawl job',
      deepCrawlFailed: 'Failed to start deep crawl',
      deepCrawlUpTo: 'Deep crawl (up to {max})',
      deepIntro: '{count, plural, one {Give one seed URL. We discover up to # page from it, trying the site’s sitemap first, then its internal links, and crawl them all.} other {Give one seed URL. We discover up to # pages from it, trying the site’s sitemap first, then its internal links, and crawl them all.}}',
      discovering: 'Discovering…',
      discoveryMethod: 'Discovery method',
      folderNameLabel: 'Folder name (optional)',
      folderNamePlaceholder: 'Auto-derived from the source if blank',
      maxPages: 'Max pages',
      maxPerJob: 'max {max} per job',
      modeAuto: 'Auto',
      modeBfs: 'BFS',
      modeHint: 'Auto = sitemap, then internal links. BFS goes one hop deeper.',
      modeLinks: 'Links',
      modeSitemap: 'Sitemap',
      pasteIntro: 'Paste up to {max} URLs, one per line. Each page is fetched and converted to clean markdown.',
      pathFilter: 'Path filter (optional)',
      seedUrl: 'Seed URL',
      sourceLabel: 'Source page (optional)',
      sourcePlaceholder: 'https://example.com/blog (used to name the folder)',
      starting: 'Starting…',
      tabDeep: 'Deep crawl',
      tabPaste: 'Paste URLs',
      title: 'Crawl into a new folder',
      urlCount: '{count, plural, one {# URL} other {# URLs}}',
    },
    crawlFailed: 'Crawl failed',
    crawledPageMeta: 'Crawled page · {date}',
    crawling: 'Crawling…',
    crawlingProgress: 'Crawling {done}/{total} pages… this updates automatically.',
    create: 'Create',
    createTitle: 'Create New Folder',
    deleteConfirmTitle: 'Delete “{name}”?',
    deleteFolder: 'Delete folder',
    deleting: 'Deleting…',
    emptyFolder: 'This folder is empty. Add transcriptions or text-to-speech outputs to organize them here.',
    exportTranscripts: 'Export transcripts',
    fromJob: 'From: {name}',
    importFromUrls: 'Import from URLs',
    importFromUrlsTitle: 'Import documents from URLs',
    inAnotherFolder: 'In another folder',
    inThisFolder: 'In this folder',
    itemCount: '{count, plural, one {# item} other {# items}}',
    kindCrawled: 'Crawled page',
    kindTranscription: 'Transcription',
    kindTts: 'Text to speech',
    loading: 'Loading…',
    loadingDocumentDescription: 'Your document will appear here.',
    loadingDocumentTitle: 'Loading document',
    loadingFolderDescription: 'The files in this folder will appear here.',
    loadingFolderTitle: 'Loading folder',
    loadingFolders: 'Loading folders…',
    loadingListDescription: 'Your folders will appear here.',
    loadingListTitle: 'Loading library folders',
    modified: 'Modified {date}',
    moveIntoNewFailed: 'Failed to move into new folder',
    moveItemsTitle: '{count, plural, one {Move # item} other {Move # items}}',
    moveToFolder: 'Move to folder',
    namePlaceholder: 'Folder name',
    newFolder: 'New folder',
    newFolderNamePlaceholder: 'New folder name',
    noFoldersCreateBelow: 'No folders yet. Create one below.',
    noMediaFound: 'No media found.',
    open: 'Open',
    openSource: 'Open source',
    pageDescription: 'Keep transcripts and generated files together so they are easy to find.',
    pageTitle: 'Library folders',
    queued: 'Queued. The worker will pick this up shortly.',
    removeConfirmBody: '{count, plural, one {The folder will be removed. Its # item won’t be deleted. It will just become unfiled.} other {The folder will be removed. Its # items won’t be deleted. They’ll just become unfiled.}}',
    removeConfirmTitle: 'Remove “{name}”?',
    removeFolder: 'Remove folder',
    removeFolderAria: 'Remove folder {name}',
    removeFromFolder: 'Remove from folder',
    removing: 'Removing…',
    rename: 'Rename',
    renameFolderAria: 'Rename folder {name}',
    renameTitle: 'Rename Folder',
    retryCrawl: 'Retry crawl',
    retrying: 'Retrying…',
    save: 'Save',
    saving: 'Saving…',
    searchMediaPlaceholder: 'Search your media...',
    searchPlaceholder: 'Search folders...',
    sortAriaLabel: 'Sort folders',
    sortByDate: 'Sort by Date',
    sortByItemCount: 'Sort by Item Count',
    sortByName: 'Sort by Name',
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
    statusPending: 'Pending',
    statusProcessing: 'Processing',
    unknownError: 'unknown error',
    updatingAutomatically: 'Updating automatically. No need to refresh.',
  },

  homeWidgets: {
    activityDescription: 'Transcription activity over the last 30 days',
    activityEmpty: 'No activity data available',
    activityTitle: 'Activity',
    activityTooltip: '{date} · {count, plural, one {# transcription} other {# transcriptions}}',
    continueWorking: 'Continue working',
    continueWorkingDescription: 'Pick up where you left off.',
    exploreTools: 'Explore tools',
    exploreToolsDescription: 'Choose another way to make something useful.',
    exportTranscripts: 'Export transcripts',
    exportTranscriptsDescription: 'Download one or many in any format.',
    moreWaysToWork: 'More ways to work',
    newTranscript: 'New transcript',
    newTranscriptDescription: 'Upload a file.',
    nextAction: 'Next action',
    nextSteps: 'Next steps',
    nextStepsDescription: 'Keep going or start another transcript.',
    organizeFolders: 'Organize folders',
    organizeFoldersDescription: 'Group transcripts and generated files.',
    quickActions: 'Quick actions',
    recordMeeting: 'Record meeting',
    recordMeetingDescription: 'Capture a live call.',
    textToSpeech: 'Text to speech',
    textToSpeechDescription: 'Create natural audio.',
    translateText: 'Translate text',
    translateTextDescription: 'Translate a transcript.',
  },

  loading: {
    exportDescription: 'Choose files and an output format.',
    exportTitle: 'Loading Export',
    inboxDescription: 'Choose a source to bring into your workspace.',
    inboxTitle: 'Loading source import',
    libraryDescription: 'Your files and folders will appear here.',
    libraryTitle: 'Loading Library',
    repurposeDescription: 'Choose an output for your episode.',
    repurposeTitle: 'Loading Create',
    supportDescription: 'Your tickets and help options will appear here.',
    supportTitle: 'Loading support',
    transcribeDescription: 'Choose a file to get started.',
    transcribeTitle: 'Loading Transcribe',
    transcriptsDescription: 'Your recent work will appear here.',
    transcriptsTitle: 'Loading transcripts',
    workspaceDescription: 'Getting things ready',
    workspaceTitle: 'Loading your workspace',
  },

  media: {
    currentTime: 'Current time',
    duration: 'Duration',
    loadError: 'This media file couldn\'t be loaded. Try downloading it or contact support.',
    loadingDetails: 'Loading media details…',
    muteAudio: 'Mute audio',
    pauseAudio: 'Pause audio',
    playAudio: 'Play audio',
    playbackBlocked: 'Playback was blocked. Press play again to start the file.',
    playbackSpeed: 'Playback speed',
    seekAudio: 'Seek audio',
    unmuteAudio: 'Unmute audio',
    volume: 'Volume',
  },

  meetings: {
    botStatus: {
      admissionTimeout: 'Not admitted in time',
      awaitingAdmission: 'Waiting to be admitted',
      cancelled: 'Cancelled',
      denied: 'Not admitted',
      dispatchFailed: 'Could not join',
      dispatched: 'Agent dispatched',
      ended: 'Meeting ended',
      failed: 'Failed',
      joining: 'Joining meeting',
      leaveTimeout: 'Recording limit reached',
      leaving: 'Leaving meeting',
      processing: 'Processing recording',
      recording: 'Recording',
      recordingPaused: 'Recording paused',
      scheduled: 'Scheduled',
      unknown: 'Status unknown',
    },
    calendar: {
      agentAttending: 'agent attending',
      emptyMonth: 'No meetings this month. Connect a calendar above to auto-detect them, or pick a day to send a Wisprs meeting agent.',
      loadNetworkError: 'Network error. Could not load this month.',
      loadPartFailed: 'Could not load part of this month.',
      moreItems: '{count, plural, one {+# more item} other {+# more items}}',
      nextMonth: 'Next month',
      pickDayHint: 'Click any day to schedule a Wisprs meeting agent for that date.',
      previousMonth: 'Previous month',
      thisMonth: 'This month',
      today: 'Today',
      tomorrow: 'Tomorrow',
      untitledAgent: 'Agent',
      wisprsAgent: 'Wisprs agent',
      wisprsAgentAttending: 'Wisprs agent attending',
    },
    calendarConnectFailed: 'Calendar connection failed. Please try reconnecting.',
    calendarConnectFailedReason: 'Calendar connection failed: {reason}. Please try reconnecting.',
    calendarConnected: '{provider} Calendar connected. Upcoming meetings will sync within 15 minutes.',
    cancel: 'Cancel',
    cancelAgentFailed: 'Failed to cancel the scheduled agent.',
    connect: {
      addAnotherAccount: 'Add another {provider} account',
      autoScheduleOff: 'auto-schedule off',
      autoScheduleOn: 'auto-schedule on',
      connectProvider: 'Connect {provider}',
      description: 'Link Google or Outlook (add more than one account if you juggle multiple calendars) and Wisprs auto-detects your upcoming meetings so the agent can join them without you pasting links.',
      lastSyncFailed: 'The last sync failed. Wisprs will keep retrying automatically.',
      lastSyncFailedDetail: 'Last sync failed: {error}',
      lastSynced: 'last synced {time}',
      lostAccess: 'Wisprs lost access to this calendar. Reconnect to resume meeting sync.',
      notAvailable: 'Calendar connection isn’t available yet. This is being set up.',
      reconnectNow: 'Reconnect now',
      startFailed: 'Couldn’t start the calendar connection. Please try again.',
      statusConnected: 'connected',
      statusDisconnected: 'disconnected',
      statusError: 'sync error',
      statusNeedsReauth: 'reconnect needed',
      title: 'Connect a calendar',
      upgradeNotice: 'Connected calendars are available on Studio and above.',
    },
    description: 'Send a Wisprs meeting agent to any Google Meet, Zoom or Teams call now, or schedule it. The transcript lands in your Library when the meeting ends.',
    dismiss: 'Dismiss',
    history: {
      consentConfirmed: 'Consent confirmed',
      durationHours: '{hours} h',
      durationHoursMinutes: '{hours} h {minutes} min',
      durationMinutes: '{minutes} min',
      durationSeconds: '{seconds} sec',
      emptyDescription: 'Send a Wisprs agent to your next meeting and the recording, transcript, and consent record will all land here.',
      emptyTitle: 'No meetings yet',
      filterAll: 'All',
      filterCompleted: 'Completed',
      filterFailed: 'Failed',
      filterInProgress: 'In progress',
      filterLabel: 'Meeting filter',
      noMatches: 'No meetings match this filter.',
      openRecording: 'Open recording',
      participants: '{count, plural, one {# person} other {# people}}',
      scheduleAgent: 'Schedule a meeting agent',
      surfaceAgent: 'Meeting agent',
      surfaceBrowser: 'Browser capture',
      surfaceMeeting: 'Meeting',
      undated: 'Undated',
    },
    howItWorks: {
      stepConsent: 'Confirm all participants know the meeting is being recorded.',
      stepDestination: 'Choose your personal library or a shared workspace destination.',
      stepPaste: 'Paste your meeting link above: Google Meet, Zoom, or Teams.',
      stepSchedule: 'Choose the meeting start time and click "Schedule Wisprs agent".',
      stepSendNow: 'Click "Send Wisprs agent". It joins within ~30 seconds.',
      stepTranscribe: 'When the meeting ends, the recording is transcribed automatically and lands in Wisprs.',
      title: 'How it works',
    },
    issue: {
      cancelled: 'cancelled upstream',
      drift: 'meeting changed',
      reconciled: 'updated from calendar',
      syncError: 'needs attention',
    },
    lockedIntro: 'Meeting agents are included from the Studio plan up.',
    lockedLink: 'Upgrade to send Wisprs to your meetings',
    lockedOutro: '. You can still browse the form and connect calendars.',
    networkError: 'Network error. Please try again.',
    openTranscript: 'Open transcript',
    ownership: {
      sharedWithWorkspace: 'shared with workspace',
      workspaceTranscript: 'workspace transcript',
      yourSession: 'your session',
    },
    personalLibrary: 'Personal library',
    personalLibraryInline: 'personal library',
    retryAgentFailed: 'Failed to retry the meeting agent.',
    schedule: {
      chooseTime: 'Choose when Wisprs should join this meeting.',
      consent: 'I confirm that all participants are aware this meeting will be recorded and transcribed by Wisprs.',
      destinationAria: 'Meeting destination',
      destinationLabel: 'Destination',
      invalidUrl: 'Must be a valid https URL.',
      meetingLinkLabel: 'Meeting link',
      nowHint: 'Immediate send is best when you are about to join or are already in the meeting.',
      optional: '(optional)',
      personalHint: 'Keep this meeting in your personal library.',
      scheduleAgent: 'Schedule Wisprs agent',
      scheduleLater: 'Schedule for later',
      scheduledHint: 'Scheduled send prepares the transcript now and sends the agent at the meeting start time.',
      scheduling: 'Scheduling agent…',
      sendAgent: 'Send Wisprs agent',
      sendNow: 'Send now',
      sending: 'Sending agent…',
      startTimeLabel: 'Start time',
      startTimeOptionalHint: 'Optional, but improves meeting identity normalization.',
      startTimeRequiredHint: 'Required for scheduled dispatch and meeting identity normalization.',
      timeInPast: 'That time is in the past. Pick an upcoming start time.',
      timingLabel: 'Send timing',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g. Weekly standup',
      workspaceHint: 'Shared workspace transcript. Same workspace requests reuse one meeting session.',
    },
    scheduledMeeting: 'Scheduled meeting',
    sessionStatus: {
      agentOnTheWay: 'Agent on the way',
      awaitingAdmission: 'Waiting to be admitted',
      cancelled: 'Cancelled',
      completed: 'Completed',
      connectionLost: 'Connection lost',
      failed: 'Failed',
      finalizing: 'Finishing up',
      partial: 'Partial recording',
      recording: 'Recording',
      recovering: 'Recovering audio',
      scheduled: 'Scheduled',
      transcribing: 'Transcribing',
    },
    somethingWentWrong: 'Something went wrong.',
    success: {
      dispatchedBody: 'The Wisprs meeting agent is joining your meeting. It will post a disclosure message when it arrives. Once the meeting ends, the transcript will appear in your library.',
      dispatchedTitle: 'Agent dispatched',
      goToTranscripts: 'Go to transcripts',
      reusedBody: 'This workspace already has an active meeting session for the same meeting. You were attached to the shared transcript instead of spawning a second agent.',
      reusedTitle: 'Meeting already tracked',
      scheduledBody: 'The meeting has been queued for automatic dispatch. Wisprs will send the agent at the scheduled join time and the transcript will land in your library after the meeting ends.',
      scheduledTitle: 'Agent scheduled',
      sendAnother: 'Send another',
    },
    tabCalendar: 'Calendar',
    tabMeetings: 'Meetings',
    tabSchedule: 'Schedule',
    title: 'Meetings',
    untitledMeeting: 'Meeting',
    upcoming: {
      confirmCancel: 'Yes, cancel',
      description: 'The latest scheduled or active agent sessions for this account.',
      emptyDescription: 'Schedule one from the form to have Wisprs join automatically later.',
      emptyTitle: 'No scheduled meeting agents yet',
      keep: 'Keep',
      noScheduledTime: 'No scheduled time',
      retry: 'Retry',
      title: 'Upcoming scheduled agents',
      wisprsMeetingAgent: 'Wisprs meeting agent',
    },
    upgradeToStudio: 'Upgrade to Studio',
    viewLabel: 'Meetings view',
  },

  notifications: {
    allCaughtUp: 'You’re all caught up.',
    justNow: 'just now',
    loading: 'Loading…',
    markAllRead: 'Mark all read',
    title: 'Notifications',
    unreadAriaLabel: '{count, plural, one {# unread notification} other {# unread notifications}}',
  },

  pages: {
    extensionConnect: {
      accessOrganize: 'Organize transcripts into folders',
      accessRead: 'Read, search and export your transcripts',
      accessSpeech: 'Generate speech from text you enter',
      accessSummarize: 'Summarize, add chapters, translate and share transcripts you open',
      accessTitle: 'The extension will be able to',
      accessTranscribe: 'Transcribe files and links you choose, against your plan',
      cancel: 'Cancel',
      connect: 'Connect',
      description: 'Lets the extension work with your Wisprs account in this browser.',
      invalidDescription: 'This link is missing details from the extension. Click Connect account in the Wisprs extension to start again.',
      invalidTitle: 'Open this from the extension',
      signedInAs: 'Signed in as {email}. Everything stays in your account. You can disconnect from the extension settings, or revoke it under Settings, API.',
      title: 'Connect Wisprs for Chrome',
    },
    inbox: {
      description: 'Bring in a YouTube video, a playlist or a podcast feed. Each becomes a transcript-ready episode in your queue.',
      title: 'Import a source',
    },
    repurpose: {
      description: 'Pick a completed episode and generate a publish-ready pack: show notes, summary, chapters, social posts or subtitles.',
      title: 'Create',
    },
    tools: {
      description: 'Downloaders, transcribers, and repurposing tools. As a signed-in user you get full-length downloads with no per-file duration or size limits.',
      title: 'Free tools',
      toolFallbackTitle: 'Free tool',
    },
    toolsHandoff: {
      backToTools: 'Back to free tools',
      close: 'Close',
      errorTitle: 'Could not import file',
      missingLink: 'Missing import link. Use the transcribe link from your completed free-tool download.',
      tryAgain: 'Please return to the tool and try again.',
    },
  },

  salesKit: {
    body: 'Body',
    copied: 'Copied!',
    copyEmail: 'Copy Email',
    copyFailedManual: 'Failed to copy to clipboard. Here\'s the text:',
    copyNote: 'Copy Note',
    copyStep: 'Copy step',
    creating: 'Creating your Sales Call Kit...',
    crmNoteDescription: 'Ready to paste into your CRM',
    crmNoteTitle: 'CRM Note',
    followUpEmailDescription: 'Ready to send',
    followUpEmailTitle: 'Follow-up Email',
    generateFailed: 'Failed to generate Sales Call Kit',
    generatingDescription: 'Generating your follow-up materials...',
    nextStepsDescription: 'Action items from the call',
    nextStepsTitle: 'Next Steps',
    regenerate: 'Regenerate',
    regenerateFailed: 'Failed to regenerate Sales Call Kit',
    regenerating: 'Regenerating...',
    subject: 'Subject',
    title: 'Sales Call Kit',
  },

  share: {
    accessStats: 'Access Statistics',
    anyoneCanDownload: 'Anyone with this link can view and download this transcript',
    anyoneCanEdit: 'Anyone with this link can view, download, and edit this transcript',
    anyoneCanView: 'Anyone with this link can view this transcript',
    cancel: 'Cancel',
    closeAria: 'Close share settings',
    copied: 'Copied!',
    copy: 'Copy',
    copyFailed: 'Failed to copy link',
    createFailed: 'Failed to create share link',
    createLink: 'Create Share Link',
    expiresNote: '(expires {date})',
    lastAccessed: 'Last accessed: {date}',
    loadFailed: 'Failed to load share settings',
    loading: 'Loading share settings...',
    passwordPlaceholder: 'Enter password',
    passwordProtect: 'Password Protect',
    passwordRequiredNote: '(password required)',
    permissionAria: 'Permission level',
    permissionDownload: 'View & Download',
    permissionDownloadDescription: 'View and Download: Can read and export the transcript',
    permissionEditDescription: 'Full Access: Can view, download, and edit the transcript',
    permissionEditFull: 'Full Access (View, Download & Edit)',
    permissionLevel: 'Permission Level',
    permissionUnknownDescription: 'Unknown permission',
    permissionView: 'View Only',
    permissionViewDescription: 'View only: Can read the transcript but cannot download or edit',
    revokeConfirm: 'Are you sure you want to revoke this share link? It will no longer be accessible.',
    revokeFailed: 'Failed to revoke share link',
    revokeLink: 'Revoke Link',
    saveFailed: 'Failed to save share settings',
    saving: 'Saving...',
    secureLink: 'Secure Link',
    setExpiration: 'Set Expiration Date',
    subtitle: 'Create a secure link to share this transcript',
    title: 'Share Transcript',
    totalViews: 'Total views: {views}',
    updateSettings: 'Update Settings',
  },

  speakers: {
    numbered: 'Speaker {n}',
    unnamed: 'Speaker',
  },

  stages: {
    cancelled: 'Transcription cancelled',
    cancelling: 'Cancelling transcription…',
    connecting: 'Connecting to transcription…',
    connectingPremium: 'Connecting to premium transcription…',
    done: 'Done',
    downloadingMedia: 'Downloading and checking your link securely… Transcription will start automatically.',
    failed: 'Transcription failed',
    finalizing: 'Finalizing transcript…',
    freeProcessing: 'Transcribing (free tier). You can leave this page; we\'ll notify you when it\'s ready.',
    freeQueued: 'Queued (free tier). Free jobs may take longer. You can leave this page; we\'ll email you when it\'s ready.',
    freeSubmitted: 'Submitted to free-tier queue. You can leave this page; we\'ll notify you when it\'s ready.',
    paidProcessing: 'Transcribing with pro models. You can leave this page; we\'ll notify you when it\'s ready.',
    paidQueued: 'Queued. You can leave this page; we\'ll notify you when it\'s ready.',
    pending: 'Preparing transcription…',
    premiumProcessing: 'Transcribing with premium accuracy…',
    preparingAudio: 'Preparing audio for transcription…',
    processing: 'Processing…',
    processingLocal: 'Processing audio locally…',
    routing: 'Selecting transcription provider…',
    startingLocal: 'Starting local transcription…',
    transcribing: 'Transcribing…',
    transcribingWith: 'Transcribing with {provider}…',
    waitingForSlot: 'Queued. It starts automatically when one of your other transcriptions finishes.',
    intakeQueued: 'Queued. Your link download starts in a moment.',
    intakeDownloadingPercent: 'Downloading your link securely: {percent}% ({done} of {total}). Transcription starts automatically.',
    intakeDownloadingBytes: 'Downloading your link securely: {done} so far. Transcription starts automatically.',
    intakeProbing: 'Download finished. Checking the audio…',
    intakeHandingOff: 'Download finished. Starting transcription…',
    intakeRetrying: 'The source didn\'t respond, so we\'re trying again (attempt {attempt} of {max}). Transcription starts automatically.',
    intakeRetryingNoCount: 'The source didn\'t respond, so we\'re trying again. Transcription starts automatically.',
  },

  support: {
    aiBadge: 'AI',
    assistantName: 'Support Assistant',
    attach: {
      add: 'Add',
      attachButton: 'Attach screenshot / video',
      fileTooLarge: '{name} is over 100MB.',
      httpsOnly: 'Links must start with https://',
      linkFallback: 'Link',
      linkPlaceholder: '…or paste a Loom / video link',
      removeAria: 'Remove attachment',
      screenshotAlt: 'screenshot',
      uploadFailed: 'Upload failed. Try a different file.',
      uploadNetworkFailed: 'Upload failed. Check your connection.',
      uploading: 'Uploading…',
    },
    attachmentsLabel: 'Attachments',
    attachmentsOptional: '(optional)',
    cancel: 'Cancel',
    closeAria: 'Close',
    closeTicket: 'Close ticket',
    closedNotice: 'This ticket is closed. Need more help? Raise a new ticket.',
    closing: 'Closing…',
    colPriority: 'Priority',
    colStatus: 'Status',
    colTicket: 'Ticket',
    colUpdated: 'Updated',
    conversationAria: 'Ticket conversation',
    createTicket: 'Create ticket',
    creating: 'Creating…',
    deletionPrefillMessage: 'I would like to request deletion of my Wisprs account and associated personal data. Please confirm the next steps.',
    deletionPrefillSubject: 'Request account deletion',
    descriptionPlaceholder: 'Share as much detail as you can: what happened, what you expected, any error messages.',
    emptyDescription: 'Hit a snag or have a question? Raise a ticket and we will get on it.',
    emptyTitle: 'No tickets yet',
    fieldDescription: 'Description',
    fieldSubject: 'Subject',
    fieldType: 'Type',
    justNow: 'just now',
    modalDescription: 'Tell us what’s going on. We’ll get back to you by email and here.',
    modalTitle: 'New support ticket',
    newTicket: 'New ticket',
    noSubject: '(no subject)',
    openTicketAria: 'Open support ticket {ref}',
    pageDescription: 'Raise an issue and track it to resolution. We reply by email and here.',
    priority: {
      high: 'High',
      low: 'Low',
      medium: 'Medium',
    },
    replyPlaceholder: 'Add a reply…',
    send: 'Send',
    sending: 'Sending…',
    status: {
      closed: 'Closed',
      open: 'Open',
      pendingFounder: 'Awaiting reply',
      replied: 'Replied',
    },
    subjectPlaceholder: 'e.g. My upload keeps failing',
    tableCaption: 'Your support tickets',
    type: {
      account: 'Account',
      billing: 'Billing',
      bug: 'Bug',
      featureRequest: 'Feature request',
      other: 'Other',
      problem: 'Problem',
      question: 'Question',
    },
    typeAria: 'Ticket type',
    unreadReply: 'Unread reply',
    widget: {
      addEmailHint: 'Add your email above so {name} can reply.',
      chatWith: 'Chat with {name}',
      closeAria: 'Close support chat',
      defaultGreeting: 'Hi! Send a message and {name} will get back to you. Stuck on a transcription, billing, or a feature idea? Just ask.',
      emailPlaceholder: 'Your email (so we can reply)',
      messagePlaceholder: 'Message the founder…',
      openAria: 'Chat with the founder',
      openSupportAria: 'Open support chat',
      replyTime: 'usually replies within a day',
    },
  },

  transcriptDetail: {
    collapse: 'Collapse',
    completedAt: 'Completed {date}',
    expand: 'Expand',
    failedBody: 'Review the recovery message below, then try the transcription again when you’re ready.',
    failedEyebrow: 'Needs attention',
    failedTitle: 'This transcript needs a retry',
    fileDetails: 'File details',
    fileInformation: 'File Information',
    fileSize: 'File Size',
    format: 'Format',
    mediaUnavailable: 'Media file not available for playback',
    originalName: 'Original: {name}',
    pendingBody: 'Choose a transcription mode below and start when you’re ready.',
    pendingEyebrow: 'Next step',
    pendingTitle: 'Your file is ready to transcribe',
    preparingBody: 'We’re downloading and checking your link securely. Transcription will start automatically.',
    preparingEyebrow: 'Preparing',
    preparingTitle: 'Preparing your media',
    processingBody: 'You can leave this page. We’ll keep processing and notify you when it’s ready.',
    processingEyebrow: 'In progress',
    processingTitle: 'We’re working on your transcript',
    readyBody: 'Read, search, export, or turn this transcript into your next piece of work.',
    readyEyebrow: 'Ready to use',
    readyTitle: 'Your transcript is ready',
    renameFile: 'Rename file',
    renameNote: 'Original upload name is kept. Storage file is not renamed.',
    save: 'Save',
    waitingBody: 'Your other transcriptions are still running. This one starts automatically when one finishes, and we’ll let you know when it’s ready.',
    waitingEyebrow: 'Queued',
    waitingTitle: 'Waiting for a free slot',
  },

  transcriptionErrors: {
    cancelledByUser: 'Cancelled by user',
    durationUnknown: 'Could not determine audio length. Try re-uploading the file.',
    generic: 'Transcription failed. Please try again or contact support.',
    retryLater: 'We couldn’t transcribe this file just now. Please try again in a few minutes. If it keeps failing, reply to support and we’ll sort it.',
    serviceUnavailable: 'Our transcription service is temporarily unavailable. Please try again in a few minutes.',
  },

  translate: {
    autoDetect: 'Auto-detect',
    charCount: '{count, plural, one {# char} other {# chars}}',
    characterCount: '{count, plural, one {# character} other {# characters}}',
    chooseDocument: 'Choose a document, or drag one here',
    chooseDocumentError: 'Please choose a document to translate',
    closeTranslation: 'Close translation',
    columnActions: 'Actions',
    columnDate: 'Date',
    columnLanguages: 'Languages',
    columnStatus: 'Status',
    copied: 'Copied',
    copy: 'Copy',
    copyFailed: 'Failed to copy to clipboard. Please try again.',
    copyText: 'Copy text',
    copyTranslatedText: 'Copy translated text',
    description: 'Text and documents (.txt, .srt, .vtt, .docx) in 100+ languages. Pin a glossary for consistent terminology.',
    detectFailed: 'Failed to detect language',
    detectSourceLanguage: 'Detect source language',
    detected: 'Detected:',
    detectingLanguage: 'Detecting language',
    documentLimits: '.txt, .md, .srt, .vtt, .docx, up to {size} MB',
    documentNote: 'Subtitle timings (.srt/.vtt) are preserved exactly. .docx keeps paragraph structure; tables and inline styling are not carried over.',
    documentTranslationFailed: 'Document translation failed',
    download: 'Download',
    downloadFailed: 'Failed to download translation. Please try again.',
    downloadFile: 'Download file',
    downloadTranslation: 'Download translation',
    downloaded: 'Downloaded',
    editable: '(editable)',
    enterTextToDetect: 'Please enter text to detect language',
    enterTextToTranslate: 'Please enter text to translate',
    glossary: {
      addTerm: 'Add term',
      cancel: 'Cancel',
      deleteFailed: 'Failed to delete glossary',
      deleteNamed: 'Delete {name}',
      editNamed: 'Edit {name}',
      empty: 'No glossaries yet. Create one to pin consistent term translations.',
      label: 'Glossary',
      loadFailed: 'Failed to load glossaries',
      loading: 'Loading glossaries...',
      manage: 'Manage',
      manageTitle: 'Manage glossaries',
      name: 'Name',
      namePlaceholder: 'e.g. Medical terms (EN→ES)',
      nameRequired: 'Glossary name is required',
      newGlossary: 'New glossary',
      noTerms: 'No terms yet. Add a source→target pair.',
      none: 'No glossary',
      removeTerm: 'Remove term',
      save: 'Save',
      saveFailed: 'Failed to save glossary',
      sourceTerm: 'Source term',
      targetTerm: 'Target term',
      termCount: '{count, plural, one {# term} other {# terms}}',
      termsHeading: 'Terms ({current}/{max})',
    },
    loadingDescription: 'Your translation workspace will appear here.',
    loadingTitle: 'Loading Translate',
    modeDocument: 'Document',
    modeLabel: 'Translation mode',
    modeText: 'Text',
    noFileToDownload: 'No file to download',
    noTextToCopy: 'No translated text to copy',
    noTextToDownload: 'No translated text to download',
    original: 'Original',
    recentTranslations: 'Recent translations',
    removeFile: 'Remove file',
    resultPlaceholder: 'The translation appears here',
    selectTargetLanguage: 'Please select a target language',
    source: 'Source',
    sourceLabel: 'Source:',
    sourceLanguage: 'Source language',
    sourcePlaceholder: 'Paste or type the text to translate',
    sourcePreview: 'Source preview',
    statusCompleted: 'Completed',
    targetLanguage: 'Target language',
    textTooLong: 'Text exceeds the maximum of {max} characters.',
    title: 'Translate',
    toneDefault: 'Default',
    toneFormal: 'Formal',
    toneInformal: 'Informal / casual',
    toneLabel: 'Tone or register',
    toneLiterary: 'Literary',
    toneTechnical: 'Technical',
    translateAction: 'Translate',
    translateAnotherDocument: 'Translate another document',
    translateDocument: 'Translate document',
    translatedLabel: 'Translated:',
    translating: 'Translating...',
    translation: 'Translation',
    translationFailed: 'Translation failed',
    translationFailedStatus: 'Translation failed ({status})',
    translationPreview: 'Translation preview',
  },

  trial: {
    activeDetails: '{count, plural, one {# day left} other {# days left}} · {minutes} premium min remaining',
    activeTitle: 'Studio trial active',
    endedBody: 'Your Studio trial ended. You’re back on Free.',
    errorNotActivated: 'Run your first transcription to unlock the free Studio trial.',
    errorUnavailable: 'Trial not available on this account.',
    getStudioBack: 'Get Studio back →',
    keepStudio: 'Keep Studio →',
    offerDetails: 'Premium accuracy, AI summaries, premium voices, no watermark. No card required.',
    offerTitle: 'Try Studio free for 7 days',
    startFreeTrial: 'Start free trial',
    starting: 'Starting…',
  },

  tts: {
    accent: 'Accent',
    age: 'Age',
    charCount: '{count, plural, one {# char} other {# chars}}',
    characterCount: '{count, plural, one {# character} other {# characters}}',
    characters: 'Characters',
    chooseVoice: 'Choose a voice',
    columnActions: 'Actions',
    columnDate: 'Date',
    columnStatus: 'Status',
    cost: 'Cost {amount}',
    costLabel: 'Cost',
    createdAt: 'Created {date}',
    delete: 'Delete',
    description: 'Turn text into natural speech.',
    detailStatusFailed: 'Needs attention',
    detailStatusProcessing: 'Making audio',
    detailStatusQueued: 'Queued',
    detailStatusReady: 'Ready',
    detailTitle: 'Speech',
    dismiss: 'Dismiss',
    download: 'Download',
    downloadAudio: 'Download audio',
    errorTitle: 'Speech could not be generated',
    failedBody: 'We encountered an error processing your text. Please try again.',
    failedTitle: 'Synthesis failed',
    gender: 'Gender',
    generateSpeech: 'Generate speech',
    generatedAudio: 'Generated audio',
    hideVoiceSettings: 'Hide voice settings',
    language: 'Language',
    likeness: 'Likeness',
    loadConversion: 'Load this conversion',
    loadingDescription: 'Your voice workspace will appear here.',
    loadingTitle: 'Loading text to speech',
    moveToFolder: 'Move to folder',
    paste: 'Paste',
    premiumVoice: 'Premium voice',
    processingBody: 'Your text is being synthesized. This may take a few moments.',
    processingTitle: 'Processing...',
    recentConversions: 'Recent conversions',
    showVoiceSettings: 'Show voice settings',
    speakerBoost: 'Speaker boost',
    speed: 'Speed',
    stability: 'Stability',
    startNew: 'Start a new text-to-speech job',
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
    statusPending: 'Pending',
    statusProcessing: 'Processing',
    style: 'Style',
    synthesisInfo: 'Synthesis information',
    synthesizeFailed: 'Failed to synthesize text',
    textContent: 'Text content',
    textLabel: 'Text',
    textPlaceholder: 'Type or paste the text to read aloud',
    title: 'Text to speech',
    tryAgain: 'Try again',
    unknownVoice: 'Unknown voice',
    unlimited: 'Unlimited',
    updatedAt: 'Updated {date}',
    usageCharacters: '{used} / {limit} characters',
    voice: 'Voice',
    voiceDescription: 'Description',
    voiceDetails: 'Voice details',
    voicePremium: '{name} (Premium)',
  },

  usage: {
    barTextToSpeechMonth: 'Text-to-speech · month',
    barTranscriptionMonth: 'Transcription · month',
    barTranscriptionToday: 'Transcription · today',
    barTranslationMonth: 'Translation · month',
    bonusAutoApplies: 'auto-applies on overshoot',
    bonusExpired: 'expired',
    bonusExpiresIn: 'expires in {time}',
    bonusMinutes: '+{value} bonus min',
    breakdownAria: 'Usage breakdown',
    charsValue: '{value} chars',
    colMinutes: 'Minutes',
    colTranscripts: 'Transcripts',
    completedOnly: 'Only completed transcripts count toward usage.',
    description: 'Transcription, text-to-speech and translation usage for this billing period.',
    durationDays: '{days}d',
    kiloCharsValue: '{value}k chars',
    lastDays: '{count, plural, one {Last # day} other {Last # days}}',
    loadingDescription: 'Your current usage will appear here.',
    loadingTitle: 'Loading usage',
    minutesValue: '{value} min',
    next: 'Next',
    noData: 'No usage data yet. Run a transcription to populate this view.',
    openPanelAria: 'Open usage panel',
    pageOf: 'Page {page} of {total}',
    paygMinutes: '{value} pay-as-you-go min',
    paygUsedAfter: 'used after your free daily minutes',
    paygValidUntil: 'valid until {date}',
    planLabel: '{plan} plan',
    previous: 'Previous',
    rangeAria: 'Usage history range',
    rangeDays: '{count, plural, one {# day} other {# days}}',
    showingRange: '{count, plural, one {Showing {start}-{end} of # day} other {Showing {start}-{end} of # days}}',
    sortAria: 'Sort by {label}; currently {direction}',
    sortAscending: 'ascending',
    sortDescending: 'descending',
    sortNone: 'not sorted',
    tableCaption: '{count, plural, one {Daily usage for the last # day} other {Daily usage for the last # days}}',
    tableEmpty: 'No completed transcripts in this period yet.',
    transcriptCount: '{count, plural, one {# transcript} other {# transcripts}}',
    viewAll: 'View all usage',
  },
} as const;
